import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getAttendanceCycle,
  getStuStatsForCurrentAttendanceCycle,
} from "../../../actions/attendance";
import {
  getEmployerAdminActivities,
  getEmployerAdminTransactionInfo,
  getStats,
  getStuStats,
} from "../../../actions/employer";
import { getAdminTodo } from "../../../actions/userInfo";
import { GOR_EMPLOYER_ID } from "../../../constants";
import {
  AdminActivity,
  AdminTodo,
  AttendanceCycleDto,
  StatsDto,
} from "../../../dto";
import Banner from "../../../images/illustration.svg";
import dispatch from "../../../middleware";
import { EmployerTransactionInfo } from "../../../models";
import { ReduxState } from "../../../reducers";
import { getMonthTypeInputValueFromDate, isEmptyString } from "../../../utils";
import getEffect from "../effect";
import Activity from "./activity";
import DashboardStats from "./dashbaordStats";
import Todo from "./todo";

const LendingProductTypes = ["EWA", "STU"] as const;
type LendingProductType = (typeof LendingProductTypes)[number];

export default function Home() {
  const storeDispatch = useDispatch();
  const employerId: string = useSelector(
    (state: ReduxState) => state.login.userDetails.employerId,
  );
  const stats: StatsDto | null = useSelector(
    (state: ReduxState) => state.employer.stats,
  );
  const stuStats = useSelector((state: ReduxState) => state.employer.stuStats);
  const adminTodo: AdminTodo[] | null = useSelector(
    (state: ReduxState) => state.login.adminTodo,
  );
  const adminActivities: AdminActivity | null = useSelector(
    (state: ReduxState) => state.employer.adminActivities,
  );
  const attendanceCycle: AttendanceCycleDto | null = useSelector(
    (state: ReduxState) => state.attendance.attendanceCycle,
  );
  const stuStatsForCurrentAttendanceCycle = useSelector(
    (state: ReduxState) => state.attendance.stuStatsForCurrentAttendanceCycle,
  );
  const fullName: string = useSelector(
    (state: ReduxState) => state.login.userDetails?.name || "",
  );
  const employerTransactionInfo: EmployerTransactionInfo | null = useSelector(
    (state: ReduxState) => state.employer.employerTransactionInfo,
  );
  const [lendingProductSelected, setLendingProductSelected] =
    useState<LendingProductType>("EWA");
  const [
    isEmployerTransactionInfoLoading,
    setIsEmployerTransactionInfoLoading,
  ] = useState<boolean>(false);
  const [selectedMonthAndYear, setSelectedMonthAndYear] = useState<Date | null>(
    new Date(),
  );
  const employerAdminEmployerId = useSelector(
    (state: ReduxState) => state.login.userDetails.employerId,
  );
  const isGorEmployerAdmin = employerAdminEmployerId === GOR_EMPLOYER_ID;

  function fetchActivities(status: boolean) {
    return dispatch(storeDispatch, getEmployerAdminActivities(status));
  }
  async function handleGetDashboardStats() {
    try {
      if (!adminTodo) dispatch(storeDispatch, getAdminTodo());
      if (!adminActivities) fetchActivities(true);
    } catch (e) {}
  }

  const fetchEmployerAdminTransactionInfo = useCallback(async () => {
    setIsEmployerTransactionInfoLoading(true);
    const monthYear = getMonthTypeInputValueFromDate(selectedMonthAndYear);
    if (!monthYear) return;
    try {
      const [year, month] = monthYear.split("-");
      await dispatch(
        storeDispatch,
        getEmployerAdminTransactionInfo({ month: "01", year: "2023" }), // INFO: Hardcoding the month and year for now
      );
    } catch (e) {
      console.log("Error fetching employer transaction info:", e);
    } finally {
      setIsEmployerTransactionInfoLoading(false);
    }
  }, [selectedMonthAndYear, storeDispatch]);

  useEffect(() => {
    if (!isEmptyString(employerId)) {
      handleGetDashboardStats();
    }
  }, [employerId]);

  useEffect(getEffect("Dashboard", useDispatch()), []);

  useEffect(() => {
    if (lendingProductSelected === "EWA") {
      if (!stats) dispatch(storeDispatch, getStats(employerId));
      if (!attendanceCycle) dispatch(storeDispatch, getAttendanceCycle());
    }
    if (lendingProductSelected === "STU") {
      if (!stuStats) dispatch(storeDispatch, getStuStats(employerId));
      if (!stuStatsForCurrentAttendanceCycle)
        dispatch(storeDispatch, getStuStatsForCurrentAttendanceCycle());
    }
  }, [lendingProductSelected]);

  useEffect(() => {
    fetchEmployerAdminTransactionInfo();
  }, [fetchEmployerAdminTransactionInfo]);

  return (
    <div>
      <div className={"row my-3"}>
        <div className={isGorEmployerAdmin ? "col-xl-11 col-lg-12 col-12" : "col-xl-8"}>
          <div className="card p-4 intro">
            <h5 className="mb-2 fs-18">Hey {fullName}!</h5>
            <p className="mb-4 fs-14 fw-300">Please complete your todo list.</p>
            <img src={Banner} className="intro-icon" alt="banner" />
          </div>

          <DashboardStats
            isEmployerTransactionInfoLoading={isEmployerTransactionInfoLoading}
            employerTransactionInfo={employerTransactionInfo}
            selectedMonthAndYear={selectedMonthAndYear}
            onMonthChange={(e) =>
              setSelectedMonthAndYear(e.currentTarget.valueAsDate)
            }
          />

          <br />
        </div>
        {!isGorEmployerAdmin && (
          <div className="col-xl-4 mt-lg-0 mt-4">
            <Activity
              activities={adminActivities}
              onShowMoreClick={() => fetchActivities(false)}
            />

            <Todo todoList={adminTodo} />
          </div>
        )}
      </div>
    </div>
  );
}
